import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import {
    getDisplayRating,
    getSellerRatingAllowPublicReviews,
    getSellerRatingTotalReviews,
} from '@/redux/modules/sellerRatings';
import { getSellerFollowerCountById } from '@/redux/modules/sellerFollowerCount';
import { H3 } from '@liveauctioneers/hammer-ui-core/text';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { openReviewsModal } from '@/redux/modules/modal';
import { RatingSection } from './components/RatingSection/RatingSection';
import { StarRating } from '@liveauctioneers/hammer-ui-core/starRating';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback } from 'react';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';

const MIN_FOLLOWERS_FOR_DISPLAY = 10;

type Props = {
    sellerId: number;
};

export const RatingContainer = ({ sellerId }: Props) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const sellerFollowerCount = useAppSelector((state) => getSellerFollowerCountById(state, sellerId));
    const allowPublicReviews = useAppSelector((state) => getSellerRatingAllowPublicReviews(state, sellerId));
    const totalReviews = useAppSelector((state) => getSellerRatingTotalReviews(state, sellerId));
    const displayRating = useAppSelector((state) => getDisplayRating(state, sellerId));

    const displayFollowers = Boolean(sellerFollowerCount >= MIN_FOLLOWERS_FOR_DISPLAY);

    const onRatingsAndReviewsClicked = useCallback(() => {
        dispatch(openReviewsModal(sellerId));
    }, [dispatch, sellerId]);

    return (
        <div className={cn('flex items-center justify-evenly gap-sm rounded-md border border-border-secondary')}>
            {allowPublicReviews && (
                <>
                    <RatingSection>
                        <H3>{totalReviews}</H3>
                        <InlineButton
                            iconPlacement="right"
                            onClick={onRatingsAndReviewsClicked}
                            size="sm"
                            style="neutral"
                        >
                            {formatMessage({ id: 'itemPage.auctionHouse.rating.reviews' })}
                        </InlineButton>
                    </RatingSection>
                    <RatingSection hasSeperator>
                        <div className="flex items-center gap-2xs">
                            <H3>{displayRating?.toFixed(1)}</H3>
                            <StarRating
                                averageRating={1}
                                emphasis="bold"
                                maxAmountOfStars={1}
                                size="sm"
                            />
                        </div>
                        <InlineButton
                            iconPlacement="right"
                            onClick={onRatingsAndReviewsClicked}
                            size="sm"
                            style="neutral"
                        >
                            {formatMessage({ id: 'itemPage.auctionHouse.rating.rating' })}
                        </InlineButton>
                    </RatingSection>
                </>
            )}
            {displayFollowers && (
                <RatingSection>
                    <H3>{sellerFollowerCount}</H3>
                    <InlineButton
                        onClick={onRatingsAndReviewsClicked}
                        size="sm"
                        style="neutral"
                    >
                        {formatMessage({ id: 'itemPage.auctionHouse.rating.followers' })}
                    </InlineButton>
                </RatingSection>
            )}
        </div>
    );
};
